import PropTypes from 'prop-types';
import React from 'react';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import {FORMATTING_SHORTCUTS} from 'components/KeyboardShortcutDescriptions';
import {ActionOrigin, ActionMethod} from 'constants/AnalyticsConstants';
import FormatState from 'notebook/mediocre/formatted-text/FormatState';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import FormattersConstants, {
  Format,
  FormatData,
  FormatDataTrigger,
} from 'notebook/mediocre/formatted-text/FormattersConstants';
import InsertLinkModalClass, {Link} from 'notebook/mediocre/hyperlinks/InsertLinkModal';
import trackInternalLink from 'notebook/mediocre/mentions/trackInternalLink';
import {trackSavedCmdKLinkModal} from 'notebook/util/analytics/KeyboardShortcutAnalytics';
import {trackKeyboardShortcut} from 'notebook/util/analytics/KeyboardShortcutAnalytics';
import {LinkedItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import ModalAnchor from 'ui-library/ModalAnchor';

type UpdateFormattedText = (
  arg1: {
    start: number;
    end: number;
    formats: Format;
    text: string;
  },
  editingFormatIndex: number
) => void;

// `link` should have type {url, text}
// If link is null this essentially becomes a no-op, and we just restore the selection (necessary
// after opening the link modal)
// If link.url is null, we remove the link
// Otherwise, we add the link
export function setLink(
  link: Link | null,
  editingLinkState: EditingLinkState,
  updateFormattedText: UpdateFormattedText
): void {
  let end;
  let newFormats;
  let text;
  const {before, after, selected, editingFormatIndex, trigger, eventGroupId} = editingLinkState;
  let formats = editingLinkState.formats;
  // Unset any explicit color so that the link can be blue
  formats = formatters.removeRange(formats, FormattersConstants.FOREGROUND_COLOR, {
    start: before.length,
    end: before.length + selected.length,
  });
  // An internal link has a name field - an external link does not
  if ((link != null && 'name' in link ? link.name : undefined) != null) {
    ({
      formats: newFormats,
      text,
      end,
    } = formatters.replaceWithInternalLink(formats, {before, after, selected}, link));
  } else {
    ({formats: newFormats, text, end} = formatters.replaceWithLink(formats, {before, after, selected}, link));
  }

  if (link != null && trigger && trigger === FormatDataTrigger.KEYBOARD_SHORTCUT) {
    trackSavedCmdKLinkModal({
      actionOrigin: ActionOrigin.NOTEBOOK_TEXT,
      actionMethod: ActionMethod.SAVE_MODAL,
      wasTextSelected: selected.length !== 0,
      eventGroupId,
    });
  }

  updateFormattedText({start: end, end, formats: newFormats, text}, editingFormatIndex);
}

function _setLinkAndClose(
  link: Link | null,
  linkedItem: LinkedItem | undefined | null,
  closeInsertLinkModal: () => void,
  editingLinkState: EditingLinkState,
  updateFormattedText: UpdateFormattedText,
  fileId: string
): void {
  if (linkedItem && link && 'id' in link && link.id) {
    trackInternalLink(fileId, link.id, linkedItem, ActionOrigin.NOTEBOOK);
  }
  setLink(link, editingLinkState, updateFormattedText);
  closeInsertLinkModal();
}

export interface EditingLinkState {
  url: string;
  before: string;
  selected: string;
  after: string;
  formats: Format;
  editingFormatIndex: number;
  trigger?: FormatDataTrigger;
  eventGroupId?: string;
}

interface EditLinkModalProps {
  showInsertLinkModal: boolean;
  editingLinkState: EditingLinkState | null;
  closeInsertLinkModal: () => void;
  fileId: string;
  updateFormattedText: UpdateFormattedText;
}

export function EditLinkModal(props: EditLinkModalProps): JSX.Element {
  return (
    <ModalAnchor open={props.showInsertLinkModal}>
      <InsertLinkModalClass
        open={props.showInsertLinkModal}
        initialText={props.editingLinkState != null ? props.editingLinkState.selected : undefined}
        initialUrl={props.editingLinkState != null ? props.editingLinkState.url : undefined}
        setLinkAndClose={(link, linkedItem) => {
          _setLinkAndClose(
            link,
            linkedItem,
            props.closeInsertLinkModal,
            props.editingLinkState!,
            props.updateFormattedText,
            props.fileId
          );
        }}
        fileId={props.fileId}
      />
    </ModalAnchor>
  );
}

EditLinkModal.propTypes = {
  showInsertLinkModal: PropTypes.bool.isRequired,
  editingLinkState: PropTypes.object,
  closeInsertLinkModal: PropTypes.func.isRequired,
  fileId: PropTypes.string.isRequired,
  updateFormattedText: PropTypes.func.isRequired,
};

export function getEditingLinkState(
  formats: Format,
  partition: {before: string; selected: string; after: string},
  editingFormatIndex: number,
  trigger?: FormatDataTrigger,
  eventGroupId?: string
): EditingLinkState {
  const {before, selected, after, url} = formatters.getSelectionForAddLink(formats, {partition});
  return {url, before, selected, after, formats, editingFormatIndex, trigger, eventGroupId};
}

export function formatText(
  formats: Format,
  {
    format,
    data,
    partition,
  }: {format: string; data?: FormatData; partition: {before: string; selected: string; after: string}},
  editingFormatIndex: number,
  openInsertLinkModal: (
    formats: Format,
    partition: {before: string; selected: string; after: string},
    editingFormatIndex: number,
    trigger?: FormatDataTrigger,
    eventGroupId?: string
  ) => void,
  updateFormattedText: UpdateFormattedText,
  trigger?: FormatDataTrigger
): void {
  // Silently ignore error if selection is undefined
  if (partition == null) {
    return;
  }
  if (partition.selected.length === 0 && FormatState.supports(format)) {
    FormatState.toggleFormat(format, data);
    return;
  }

  if (format === FormattersConstants.LINK) {
    // Every time the user opens the Link modal, create a new eventGroupId to track the series of
    // actions taken (opening vs saving the modal).
    const eventGroupId: string = makeAnalyticsEventId();
    // Because the pathways for inserting links on an editable note part (text) and on
    // a table cell are different, we can necessarily claim that this route's source was
    // obtained by calling EditableComponent's formatHandler.
    if (trigger && trigger === FormatDataTrigger.KEYBOARD_SHORTCUT) {
      const shortcutDesc = FORMATTING_SHORTCUTS.find((s) => s.description === 'Link');
      if (shortcutDesc) {
        trackKeyboardShortcut(shortcutDesc, {
          actionOrigin: ActionOrigin.NOTEBOOK_TEXT,
          actionMethod: ActionMethod.OPEN_MODAL,
          wasTextSelected: partition.selected.length !== 0,
          eventGroupId,
        });
      }
    }
    openInsertLinkModal(formats, partition, editingFormatIndex, trigger, eventGroupId);
    // TODO(eln) - T1077 Ideally here, we set a fake selection on the editor to reflect which
    // part is actually getting edited. This is necessary when you partially select a
    // link, then try to edit it.
  } else if (format === FormattersConstants.SET_LINK) {
    const editingLinkState = getEditingLinkState(formats, partition, editingFormatIndex);
    setLink(
      {text: data?.text ?? editingLinkState.selected, url: data?.url!, type: 'external'},
      editingLinkState,
      updateFormattedText
    );
    // formats includes all supported formats, so silently ignore if it is not present.
  } else if (format in formats) {
    let end;
    let start;
    let text;
    ({formats, text, start, end} = formatters.formatText(formats, {format, data, partition}));
    updateFormattedText({formats, text, start, end}, editingFormatIndex);
  }
}
